/**
 * SOP library — the list of process flows with each person's read / watched
 * status and, for management, the compliance view entry point.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  BookOpen, Loader2, ChevronRight, CheckCircle2, PlayCircle, Film, FileDown, Mic2,
  Search, Play, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr } from '../../../components/admin/billing/shared';

const SopLibraryPage = () => {
  const { authHeader } = useAuth();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voice, setVoice] = useState(null);   // { configured, voices, selected }
  const [savingVoice, setSavingVoice] = useState('');
  const [library, setLibrary] = useState(null);
  const [libQuery, setLibQuery] = useState('bengali');
  const [libBusy, setLibBusy] = useState('');
  const [langs, setLangs] = useState([]);
  const [voiceLang, setVoiceLang] = useState('bn');

  useEffect(() => {
    axios.get(`${API}/sop/languages`, { headers: authHeader() })
      .then(({ data }) => setLangs((data.languages || []).filter((l) => l.code !== 'en')))
      .catch(() => setLangs([]));
  }, [authHeader]);

  const searchLibrary = async (q) => {
    setLibBusy('search');
    try {
      const { data } = await axios.get(`${API}/sop/voices/library`, {
        headers: authHeader(), params: { q },
      });
      setLibrary(data.voices || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLibBusy(''); }
  };

  const addLibraryVoice = async (v) => {
    setLibBusy(v.voice_id);
    try {
      const { data } = await axios.post(`${API}/sop/voices/library`, {
        public_owner_id: v.public_owner_id, voice_id: v.voice_id, name: v.name,
        lang: voiceLang,
      }, { headers: authHeader() });
      toast.success(`${data.name} is now the narrator for ${langLabel(voiceLang)}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally {
      try {
        const { data: vs } = await axios.get(`${API}/sop/voices`, { headers: authHeader() });
        setVoice(vs);
      } catch { /* dropdown keeps its last known state */ }
      setLibBusy('');
    }
  };

  useEffect(() => {
    axios.get(`${API}/sop/voices`, { headers: authHeader() })
      .then(({ data }) => setVoice(data))
      .catch(() => setVoice(null));
  }, [authHeader]);

  const langLabel = (code) => (langs.find((l) => l.code === code) || {}).label || code;

  const pickVoice = async (lang, voiceId) => {
    const chosen = (voice?.voices || []).find((v) => v.voice_id === voiceId);
    setSavingVoice(lang);
    try {
      await axios.patch(`${API}/sop/settings`, {
        lang, el_voice_id: voiceId, el_voice_name: chosen?.name || '',
      }, { headers: authHeader() });
      setVoice((p) => ({
        ...p, selected_by_lang: { ...(p.selected_by_lang || {}), [lang]: voiceId },
      }));
      toast.success(`${chosen?.name || 'Voice'} will now narrate ${langLabel(lang)}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSavingVoice(''); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sop/flows`, { headers: authHeader() });
      setFlows(data.flows || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const pdf = async (f) => {
    try {
      const res = await axios.get(`${API}/sop/flows/${f.id}/pdf`, {
        headers: authHeader(), responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-teal-600" /></div>;
  }

  return (
    <div className="space-y-5 pb-20" data-testid="sop-library-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <BookOpen size={20} className="text-teal-600" /> SOP &amp; Process Flow
        </h1>
        <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">
          How a customer moves through Unisol Global, stage by stage — what happens, why it
          matters, and the checklist for each stage. Read it, or let it be read to you.
        </p>
      </div>

      {voice?.configured && (
        <div className="bg-white border border-slate-200 rounded-xl p-3.5" data-testid="voice-settings">
          <p className="inline-flex items-center gap-1.5 text-sm text-slate-700 mb-2.5">
            <Mic2 size={15} className="text-teal-600" /> Narration voice per language
            <span className="text-[11px] text-slate-400">
              English uses the built-in voice · a recorded voiceover on a stage always wins
            </span>
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            {langs.map((l) => (
              <div key={l.code} className="flex items-center gap-2" data-testid={`voice-row-${l.code}`}>
                <span className="w-20 shrink-0 text-xs text-slate-600">
                  {l.native}
                </span>
                <select
                  value={(voice.selected_by_lang || {})[l.code] || voice.selected || ''}
                  disabled={savingVoice === l.code}
                  onChange={(e) => pickVoice(l.code, e.target.value)}
                  className="flex-1 min-w-0 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-teal-500 focus:outline-none"
                  data-testid={`native-voice-select-${l.code}`}
                >
                  {(voice.voices || []).map((v) => (
                    <option key={v.voice_id} value={v.voice_id}>
                      {v.name}{v.labels?.accent ? ` — ${v.labels.accent}` : ''}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => { setVoiceLang(l.code); setLibQuery(l.label.toLowerCase()); searchLibrary(l.label.toLowerCase()); }}
                  className="shrink-0 rounded-lg border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50"
                  title={`Find a native ${l.label} voice`}
                  data-testid={`find-voice-${l.code}`}
                >
                  {libBusy === 'search' && voiceLang === l.code
                    ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
                </button>
              </div>
            ))}
          </div>

          {library && (
            <div className="w-full mt-3 border-t border-slate-100 pt-3" data-testid="voice-library">
              <p className="text-xs text-slate-500 mb-1.5">
                Picking one here sets it as the <b>{langLabel(voiceLang)}</b> narrator.
              </p>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <input
                  value={libQuery} onChange={(e) => setLibQuery(e.target.value)}
                  placeholder={`Search the ElevenLabs library for a ${langLabel(voiceLang)} voice`}
                  className="flex-1 min-w-[220px] rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
                  data-testid="voice-library-query"
                />
                <button
                  onClick={() => searchLibrary(libQuery)}
                  className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs text-white hover:bg-slate-800"
                  data-testid="voice-library-search"
                >
                  Search
                </button>
                <button onClick={() => setLibrary(null)} className="text-xs text-slate-500 hover:text-slate-800">
                  Close
                </button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 max-h-72 overflow-y-auto">
                {library.map((v) => (
                  <div key={v.voice_id} className="border border-slate-200 rounded-lg p-2.5" data-testid={`library-voice-${v.voice_id}`}>
                    <p className="text-sm text-slate-900 leading-snug">{v.name}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {[v.accent, v.gender, v.age].filter(Boolean).join(' · ')}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      {v.preview_url && (
                        <button
                          onClick={() => { const a = new Audio(v.preview_url); a.play(); }}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50"
                          data-testid={`preview-${v.voice_id}`}
                        >
                          <Play size={11} /> Preview
                        </button>
                      )}
                      <button
                        onClick={() => addLibraryVoice(v)} disabled={libBusy === v.voice_id}
                        className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-2 py-1 text-[11px] text-white hover:bg-teal-700 disabled:bg-slate-300"
                        data-testid={`use-voice-${v.voice_id}`}
                      >
                        {libBusy === v.voice_id ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                        Use this voice
                      </button>
                    </div>
                  </div>
                ))}
                {library.length === 0 && (
                  <p className="text-sm text-slate-500">No voices matched that search.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {flows.map((f) => (
          <div
            key={f.id}
            className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col hover:border-teal-300 transition-colors"
            data-testid={`sop-card-${f.key}`}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-[11px] uppercase tracking-wider text-teal-700 font-semibold">
                {f.short_title || 'Process flow'}
              </span>
              <span className="text-[11px] text-slate-400">v{f.version}</span>
            </div>
            <h2 className="mt-1.5 text-base font-semibold text-slate-900 leading-snug">{f.title}</h2>
            <p className="mt-1.5 text-sm text-slate-500 flex-1">{f.subtitle}</p>

            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="rounded-full bg-slate-100 text-slate-600 text-[11px] px-2 py-0.5">
                {f.stage_count} stages
              </span>
              {f.clips > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 text-[11px] px-2 py-0.5">
                  <Film size={11} /> {f.clips} clip{f.clips === 1 ? '' : 's'}
                </span>
              )}
              {f.read_at && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] px-2 py-0.5" data-testid={`read-badge-${f.key}`}>
                  <CheckCircle2 size={11} /> Read
                </span>
              )}
              {f.watched_at && (
                <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 text-[11px] px-2 py-0.5" data-testid={`watched-badge-${f.key}`}>
                  <PlayCircle size={11} /> Watched
                </span>
              )}
            </div>

            <div className="mt-4 flex items-center gap-2">
              <Link
                to={`/admin/sop/${f.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-teal-700"
                data-testid={`open-flow-${f.key}`}
              >
                Open flow <ChevronRight size={15} />
              </Link>
              <button
                onClick={() => pdf(f)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                data-testid={`pdf-${f.key}`}
              >
                <FileDown size={15} /> PDF
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              Updated {(f.updated_at || '').slice(0, 10) || '—'}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SopLibraryPage;
